import React from "react";
import { Label } from "@/components/ui/label";
import type { OnboardingData } from "@/pages/Onboarding";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  CircleDashed,
  DollarSignIcon,
  GiftIcon,
  LinkIcon,
  MousePointerClickIcon,
  PercentIcon,
  Wand2Icon,
  X,
} from "lucide-react";
import { MultiStepLoader } from "@/components/ui/multi-step-loader";
import { useCreatorsStore, useCampaignStore } from "@/stores";
import { useAuth, useOrganization, useUser } from "@clerk/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Creator } from "@/types/types";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import AmyBadge from "@/components/ui/amy-badge";
import posthog from "posthog-js";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface SearchCreatorsProps {
  formData: OnboardingData;
  updateFormData: (data: Partial<OnboardingData>) => void;
  handleNext: () => void;
}

const loadingStates = [
  {
    text: "Analysing 1000s of creators 🔍",
  },
  {
    text: "Identifying exactly where they are good at 🎯",
  },
  {
    text: "Analysing campaign details 📊",
  },
  {
    text: "Matching relevancy with your request 🔗",
  },
  {
    text: "Matching Performance campaign details 📈",
  },
  {
    text: "Recommending Best Matches 🤝",
  },
];

const exampleQuery =
  "Tech creators who love to show their desk setup and gadgets.";

const partnerships = [
  {
    value: "affiliate",
    label: "Affiliate",
    description: "Creators earn commissions for driving sales.",
    icon: <LinkIcon />,
  },
  {
    value: "paid",
    label: "Paid",
    description: "Creators receive a fixed payment for collaboration.",
    icon: <DollarSignIcon />,
  },
  {
    value: "gifting",
    label: "Gifting",
    description: "Creators recieve the product as payment.",
    icon: <GiftIcon />,
  },
];

const affiliate_compensation_types = [
  {
    value: "revenue_share",
    label: "Revenue Share",
    description: "Percentage of revenue shared with creators.",
    icon: <PercentIcon />,
  },
  {
    value: "cpa",
    label: "CPA (Cost Per Action)",
    description: "Fixed payment per action.",
    icon: <MousePointerClickIcon />,
  },
  {
    value: "other",
    label: "Other",
    description: "Custom compensation structure.",
    icon: <CircleDashed />,
  },
];

export default function SearchCreators({
  formData,
  updateFormData,
  handleNext,
}: SearchCreatorsProps) {
  const [loading, setLoading] = React.useState(false);
  const [partnershipType, setPartnershipType] = React.useState("affiliate");
  const [affiliateCompensationType, setAffiliateCompensationType] =
    React.useState("cpa");
  const [value, setValue] = React.useState("25");
  const { getToken } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { fetchCreators, creators, reset } = useCreatorsStore();
  const { campaignId, setCampaignId } = useCampaignStore();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
  };

  const searchCreators = async () => {
    if (loading) return;

    try {
      reset();
      setLoading(true);
      const token = await getToken();

      if (!token || !user?.id || !organization?.id) {
        console.error("Authentication error");
        setLoading(false);
        return;
      }

      const response1 = await fetch("https://eu.posthog.com/batch/")
        .then((r) => console.log("EU works:", r.status))
        .catch((e) => console.log("EU failed:", e));

      console.log(response1);

      posthog.capture("onboarding_search_creators", {
        distinct_id: user.id,
        organization_id: organization.id,
        properties: {
          search_creators_query: formData.search_creators_query,
          partnership_type: partnershipType,
          affiliate_compensation_type: affiliateCompensationType,
          value,
        },
      });

      const data_to_send = {
        campaign_name: "On-Boarding Campaign",
        follower_range_from: 0,
        follower_range_to: 1000000000,
        number_of_creators: 2,
        countries: ["US"],
        creator_prompt: formData.search_creators_query,
        platform: "tiktok",
        partnership: partnershipType,
        max_budget_per_creator: value,
        affiliate_compensation_type: affiliateCompensationType,
        exclude_past_campaign_creators: false,
        use_creator_native_language: false,
        enable_smart_negotiation: false,
        cpa: value,
        revenue_share: value,
        other_compensation: value,
        user_id: user.id,
        organization_id: organization.id,
        fields: [
          {
            name: "Name",
            type: "string",
            explanation: "",
            enabled: true,
            description:
              "The influencer/creator's full name (first AND last name).",
          },
          {
            name: "Phone",
            type: "string",
            explanation: "",
            enabled: true,
            description:
            "The influencer/creator's phone number including the country code.",
          },
          {
            name: "Address",
            type: "string",
            explanation: "",
            enabled: true,
            description:
              "The influencer/creator's address for shipping products or sending physical materials needed for the campaign.",
          },
          {
            name: "Billing-Info",
            type: "string",
            explanation: "",
            enabled: true,
            description:
              "The influencer/creator's payment details. Only payment methods explicity listed in the context e.g. IBAN, BANK, PAYPAL etc.. Not the actual number.",
          },
        ],
        onboarding: true,
      };

      const create_campaign_response = await fetch(
        `${API_BASE_URL}/api/campaigns/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data_to_send),
        }
      );

      const data = await create_campaign_response.json();
      if (!create_campaign_response.ok) {
        console.error("Failed to create campaign:", data);
        setLoading(false);
        return;
      }

      setCampaignId(data.campaign_id);
    } catch (error) {
      console.error("Error searching creators:", error);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!campaignId || !loading) return;

    const poll = async () => {
      console.log(
        "[poll] ⏳ fetching creators…",
        new Date().toLocaleTimeString()
      );
      const token = await getToken();
      if (!token) return;

      await fetchCreators(campaignId, token);

      const { creators: updated } = useCreatorsStore.getState();
      if (updated.length > 0) {
        setLoading(false);
      }
    };

    poll();

    const intervalId = setInterval(poll, 5000);
    return () => {
      clearInterval(intervalId);
    };
  }, [campaignId, loading, getToken, fetchCreators]);

  return (
    <div className="w-full max-w-2xl">
      <div className="w-full">
        {creators?.length === 0 && (
          <div className="space-y-2">
            <h1 className="text-2xl font-medium flex flex-row items-center gap-1">
              Let
              <AmyBadge />
              find you 10s of 🇺🇸 creators for free!
            </h1>
            <div className="space-y-2 relative mt-6">
              <Label htmlFor="search_creators_query">
                Describe your ideal TikTok creator.
              </Label>
              <Textarea
                id="search_creators_query"
                name="search_creators_query"
                placeholder={exampleQuery}
                className="min-w-2xl h-28 pb-10 p-3 pl-4 relative overflow-hidden resize-none rounded-xl"
                value={formData.search_creators_query}
                onChange={handleChange}
                autoFocus
                required
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchCreators();
                  }
                }}
              />
              <div className="absolute bottom-2 right-2 flex items-center gap-2">
                {formData.search_creators_query && (
                  <Button
                    className="group rounded-sm"
                    size="sm"
                    variant="destructive-ghost"
                    onClick={() => {
                      updateFormData({ search_creators_query: "" });
                    }}
                  >
                    <X />
                    Clear
                  </Button>
                )}
                <Button
                  className="group rounded-sm"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    updateFormData({ search_creators_query: exampleQuery });
                  }}
                >
                  <Wand2Icon className="group-hover:-rotate-12 transition-all" />
                  Try example
                </Button>
                <Button
                  disabled={!formData.search_creators_query || loading}
                  size="sm"
                  className="rounded-sm"
                  onClick={searchCreators}
                >
                  Find them!
                  <ArrowRightIcon className="group-hover:-rotate-45 transition-all group-active:translate-x-0.5 group-active:-translate-y-0.5" />
                </Button>
              </div>
            </div>

            <Label className="mt-5">Choose your partnership type</Label>
            <div className="flex flex-row items-center gap-2">
              <Select
                value={partnershipType}
                onValueChange={(value) => setPartnershipType(value)}
              >
                <SelectTrigger className="max-w-1/2 w-full h-8 text-xs">
                  {partnershipType ? (
                    <div className="flex items-center gap-2 font-medium">
                      {
                        partnerships.find((p) => p.value === partnershipType)
                          ?.icon
                      }
                      {
                        partnerships.find((p) => p.value === partnershipType)
                          ?.label
                      }
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Select</span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  {partnerships.map((partnership) => (
                    <SelectItem
                      key={partnership.value}
                      value={partnership.value}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 font-medium">
                          {partnership.icon}
                          {partnership.label}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {partnership.description}
                        </p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {partnershipType === "affiliate" && (
                <Select
                  value={affiliateCompensationType}
                  onValueChange={(value) => setAffiliateCompensationType(value)}
                >
                  <SelectTrigger className="w-full h-8">
                    {affiliateCompensationType ? (
                      <div className="flex items-center gap-2 font-medium text-xs">
                        {
                          affiliate_compensation_types.find(
                            (t) => t.value === affiliateCompensationType
                          )?.icon
                        }
                        {
                          affiliate_compensation_types.find(
                            (t) => t.value === affiliateCompensationType
                          )?.label
                        }
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Select</span>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {affiliate_compensation_types.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 font-medium">
                            {type.icon}
                            {type.label}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {type.description}
                          </p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {partnershipType != "gifting" && (
                <div className="relative w-full">
                  {partnershipType === "paid" && (
                    <Label className="absolute -top-5 left-0">Max Budget Per Creator</Label>
                  )}
                  <Input
                    className={`peer h-8 w-full ${affiliateCompensationType === "other" ? "" : "pe-9"
                      }`}
                    placeholder={
                      affiliateCompensationType === "other"
                        ? "$40 cpa, free product"
                        : "value"
                    }
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  {affiliateCompensationType != "other" && (
                    <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 peer-disabled:opacity-50">
                      {partnershipType === "affiliate" &&
                        affiliateCompensationType === "revenue_share" ? (
                        <PercentIcon className="h-4 w-4" />
                      ) : (
                        <DollarSignIcon className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {campaignId && creators.length && (
        <div className="flex flex-col items-start space-y-2 pb-20">
          <h1 className="text-2xl font-medium flex flex-row items-center justify-center gap-1">
            <AmyBadge /> found these creators . . .
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            <motion.div
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.05 } },
              }}
              initial="hidden"
              animate="visible"
              className="contents"
            >
              {creators.slice(0, 20).map((creator: Creator, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  <Link
                    to={`https://tiktok.com/@${creator.platform_id}`}
                    target="_blank"
                    className="w-full"
                    onClick={() => {
                      posthog.capture("onboarding_creator_clicked", {
                        distinct_id: user?.id,
                        organization_id: organization?.id,
                        properties: {
                          creator_username: creator.username,
                        },
                      });
                    }}
                  >
                    <Button
                      variant="outline"
                      className="h-auto px-3 py-3 flex flex-row justify-start gap-2 w-full group"
                    >
                      <img
                        src={creator.avatar}
                        alt={`${creator.username}'s profile picture`}
                        className="size-12 rounded-pill object-cover border shrink-0 group-hover:hidden"
                      />
                      <div className="hidden group-hover:flex items-center justify-center size-12 bg-background border border-dashed shrink-0 rounded-pill text-muted-foreground">
                        <ArrowUpRightIcon className="size-6" />
                      </div>
                      <div className="flex flex-col items-start justify-center">
                        <span className="font-medium text-sm">
                          @{creator.username}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {creator.followers} followers
                        </span>
                      </div>
                    </Button>
                  </Link>
                </motion.div>
              ))}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: { opacity: 1, y: 0 },
                }}
                className="border border-dashed rounded-md p-2 text-center bg-muted flex items-center justify-center text-xs"
              >
                + {creators.length - 5}0 more of those . . .
              </motion.div>
            </motion.div>
          </div>
          <h1 className="text-2xl font-medium mt-10 gap-2 flex flex-row items-center justify-center">
            Let's prepare her to
            <span className=" text-primary">outreach to them!</span>
          </h1>
          <Button
            className="w-full group"
            disabled={loading}
            onClick={() => handleNext()}
          >
            Make FAQ and Brief !
            <ArrowRightIcon className="group-hover:translate-x-0.5 group-active:translate-x-1 transition-all" />
          </Button>
        </div>
      )}

      <MultiStepLoader loadingStates={loadingStates} loading={loading} />
    </div>
  );
}
